import { adminActionButtonSx } from 'src/theme/adminActionButton';
import {
  Avatar,
  Tooltip,
  IconButton,
  Box,
  Button,
  styled,
  InputBase,
  useTheme,
  CircularProgress,
  Icon,
  Divider,
  Card,
  InputAdornment,
  TextField,
  Typography
} from '@mui/material';
import AttachFileTwoToneIcon from '@mui/icons-material/AttachFileTwoTone';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import SendTwoToneIcon from '@mui/icons-material/SendTwoTone';
import { config } from 'src/config';
import { sendPlatformSupportReply, uploadPlatformSupportAttachment, sendChatMessage, uploadChatAttachment } from 'src/queries/chat/api';
import { readChatSessionIdentity } from 'src/auth/sessionEvents';
import { useLayoutEffect, useRef, useState } from 'react';
import { sendMessage, uploadFile } from 'src/helper/backend_helper';
import { CancelOutlined } from '@mui/icons-material';
import { queryClient } from 'src';
import { chatKeys } from 'src/queries/chat/keys';
import { TICKET_SUBJECT_MAX } from 'src/models/chat';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
// import { socket } from './ChatContent';
import PLS from '../../../assets/images/PLS.png';

const MessageInputWrapper = styled(InputBase)(
  ({ theme }) => `
    font-size: ${theme.typography.pxToRem(18)};
    padding: ${theme.spacing(1)};
    width: 100%;
`
);

const Input = styled('input')({
  display: 'none'
});

const messageDrafts = new Map<string, string>();
const subjectDrafts = new Map<string, string>();
const pendingSendDrafts = new Map<string, string>();
function loadDraft(map: Map<string, string>, key: string, field: string) {
  try { return sessionStorage.getItem(`message-draft:${key}:${field}`) ?? map.get(key) ?? ''; }
  catch { return map.get(key) || ''; }
}
function saveDraft(map: Map<string, string>, key: string, field: string, value: string) {
  map.set(key, value);
  try { if (value) sessionStorage.setItem(`message-draft:${key}:${field}`, value); else sessionStorage.removeItem(`message-draft:${key}:${field}`); }
  catch { /* In-memory drafts remain usable when storage is unavailable. */ }
}

function BottomBarContent({
  chat,
  messages,
  setMessages,

  scrollToBottom,
  socket,
  supportScope = 'legacy',
  canonicalTransport = false,
  sendContext = undefined,
  onSent = undefined,
  compact = false,
  draftScope = ''
}) {
  const cache = useQueryClient();
  const theme = useTheme();
  const sessionIdentity = readChatSessionIdentity();
  const requireCurrentSession = () => {
    if (readChatSessionIdentity() !== sessionIdentity) {
      throw new Error('Chat session changed; reopen the conversation.');
    }
  };
  const ref: any = useRef();

  const user = JSON.parse(localStorage.getItem('PLS'))?.user;

  const [media, setMedia] = useState(null);
  const draftKey = `${readChatSessionIdentity(false)}:${draftScope}:${chat.id}:${sendContext?.topicKey || JSON.stringify(sendContext?.source || sendContext?.ticket || {})}`;
  const [message, setMessageState] = useState(() => loadDraft(messageDrafts, draftKey, 'message'));
  useLayoutEffect(() => setMessageState(loadDraft(messageDrafts, draftKey, 'message')), [draftKey]);
  const setMessage = (value: string) => { saveDraft(messageDrafts, draftKey, 'message', value); setMessageState(value); };
  const [messageType, setMessageType] = useState('text');
  const [showMediaPopup, setShowMediaPopup] = useState(false);
  // #4005: a `ticket` context starts a new ticket; the company may name it.
  const startsTicket = !!sendContext?.ticket;
  const [subject, setSubjectState] = useState(() => loadDraft(subjectDrafts, draftKey, 'subject'));
  const setSubject = (value: string) => { saveDraft(subjectDrafts, draftKey, 'subject', value); setSubjectState(value); };
  useLayoutEffect(() => setSubjectState(loadDraft(subjectDrafts, draftKey, 'subject')), [draftKey]);
  // A resend of the same text reuses its ID, so the server returns the first copy
  // instead of storing it twice (or opening a second ticket) after a timeout.
  const pendingSend = useRef<{ key: string; id: string } | null>(null);
  const clientMessageIdFor = (key: string) => {
    if (pendingSend.current?.key !== key) {
      try { pendingSend.current = JSON.parse(loadDraft(pendingSendDrafts, draftKey, 'send') || 'null'); } catch { pendingSend.current = null; }
    }
    if (pendingSend.current?.key !== key) {
      pendingSend.current = { key, id: globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}` };
    }
    saveDraft(pendingSendDrafts, draftKey, 'send', JSON.stringify(pendingSend.current));
    return pendingSend.current.id;
  };

  const uploadedAttachment = useRef<{ file: File; response: any; scope: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [sendError, setSendError] = useState('');

  const handleSendMessage = async () => {
    // event.preventDefault();

    if (loading || (!message && !media)) {
      return;
    }
    try {
      requireCurrentSession();
      // const receiver = chat.participants.find((e) => e.userId !== user.id);

      // #403: senderId is derived from the JWT on the backend. Sending
      // it from the client is now a 400. Identity stays implicit.
      const data = {
        chatId: chat.id,
        content: message,
        type: messageType
      };


      const context = startsTicket
        ? { ticket: { ...(sendContext?.ticket || {}), ...(subject.trim() ? { subject: subject.trim() } : {}) } }
        : sendContext;
      let sent;
      const headersJson = {
        headers: {
          'content-type': 'application/json'
        }
      };
      setSendError('');
      setLoading(true);
      if (messageType === 'text') {
        sent = await (supportScope === 'platform' ? sendPlatformSupportReply(chat.id, { content: data.content, type: data.type }) : canonicalTransport ? sendChatMessage(chat.id, { content: data.content, type: data.type, clientMessageId: clientMessageIdFor(`${chat.id}|${data.content}|${JSON.stringify(context ?? null)}`), ...(context ? { context } : {}) }) : sendMessage(data, headersJson));
        pendingSend.current = null;
        saveDraft(pendingSendDrafts, draftKey, 'send', '');
        // #413: the server now emits `chat:message:new` after the REST
        // call persists the message, so the round-trip
        // `socket.emit('message', ...)` is gone. ChatContent.tsx's
        // existing `chat:message:new` subscription delivers the
        // server's canonical copy to all room members (including this
        // one) moments after the POST resolves.

        setMessage('');
      }

      if (messageType !== 'text') {
        const headers = {
          headers: {
            'content-type': 'multipart/form-data'
          }
        };

        const formData = new FormData();
        formData.append('file', media);
        const responseFile = uploadedAttachment.current?.file === media && uploadedAttachment.current.scope === draftKey ? uploadedAttachment.current.response : await (supportScope === 'platform' ? uploadPlatformSupportAttachment(chat.id, formData) : canonicalTransport ? { data: { data: await uploadChatAttachment(chat.id, media) } } : uploadFile(formData, headers));
        requireCurrentSession();
        uploadedAttachment.current = { file: media, response: responseFile, scope: draftKey };
        data.content = supportScope === 'platform' || canonicalTransport ? `chat-attachments/${responseFile.data.data.storageKey}` : responseFile.data.data.filename;
        sent = await (supportScope === 'platform' ? sendPlatformSupportReply(chat.id, { content: data.content, type: data.type, metadata: { attachmentIds: [responseFile.data.data.id] } }) : canonicalTransport ? sendChatMessage(chat.id, { content: data.content, type: data.type, clientMessageId: clientMessageIdFor(`${chat.id}|${data.content}|${JSON.stringify(context ?? null)}`), metadata: { attachmentIds: [responseFile.data.data.id] }, ...(context ? { context } : {}) }) : sendMessage(data, headersJson));
        // #413: same removal as the text path — the server emits
        // `chat:message:new` once persistence completes.

        pendingSend.current = null;
        saveDraft(pendingSendDrafts, draftKey, 'send', '');
        uploadedAttachment.current = null;
        setMedia(null);
        setShowMediaPopup(false);
        setMessageType('text');
      }
      requireCurrentSession();
      if (canonicalTransport || supportScope === 'platform') {
        cache.invalidateQueries(chatKeys.chatMessages(chat.id));
        cache.invalidateQueries(chatKeys.myChatsRoot());
        cache.invalidateQueries(chatKeys.messageCenter());
        if (sendContext !== undefined) {
          cache.invalidateQueries(chatKeys.chatTopics(chat.id));
          cache.invalidateQueries(chatKeys.eventThreadsRoot());
        }
        if (supportScope === 'platform') cache.invalidateQueries(chatKeys.supportInboxRoot());
      }
      setLoading(false);
      setSubject('');
      onSent?.(sent?.message);
      scrollToBottom();
      // event.target.reset();
      // setSendMessageLoading(false);
      updateChatMutation.mutate('');
    } catch (error) {
      setLoading(false);
      if (canonicalTransport) setSendError('Message could not be sent. Your draft is saved. Select Send to retry.');
      else toast.error(error?.response?.data?.message || error.message, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'light'
      });
    }
  };

  const updateChatMutation = useMutation(
    (updatedJobData: any) => {
      return updatedJobData;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['chats']);
      },
      onError: (error) => {
        console.error('Error updating job:', error); // Optional: Handle errors
      }
    }
  );

  function handleSelectMedia(file) {
    // console.log(file);
    if (file) {
      setMedia(file);
      const type = file.type.split('/')[0];

      setMessageType(type);
      // if (type === 'audio') {
      //   setMessageType('audio');
      // }
      if (type === 'application') {
        setMessageType('file');
      }
      return setShowMediaPopup(true);
    }
    setMedia(null);
    setShowMediaPopup(false);
    setMessageType('text');
  }

  function handleMediaCancel() {
    setMedia(null);
    setShowMediaPopup(false);
    setMessageType('text');
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault(); // Prevent line break on Enter
      handleSendMessage();
    }
  };

  return (
    <Box
      sx={
        {
          // p: 2
        }
      }
    >
      {sendError && <Typography role="alert" color="error" variant="caption" sx={{ px: 2, display: 'block' }}>{sendError}</Typography>}
      {messageType === 'image' && media && (
        <Card sx={{ position: 'relative', display: 'flex', m: 2, width: 150 }}>
          <img
            src={URL.createObjectURL(media)}
            alt="message_image"
            width={150}
          />

          <Tooltip arrow placement="top" title="Cancel">
            <label htmlFor="messenger-upload-file">
              <IconButton
                color="primary"
                component="span"
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0

                  // '&:hover': {
                  //   backgroundColor: 'gray'
                  // }
                }}
                onClick={() => handleMediaCancel()}
              >
                <CancelOutlined fontSize="small" />
              </IconButton>
            </label>
          </Tooltip>
        </Card>
      )}

      {messageType !== 'image' && media && (
        <Card
          sx={{
            position: 'relative',
            display: 'flex',
            m: 1,
            p: 3,
            maxWidth: 300
          }}
        >
          <small>
            {media.name.length > 30
              ? `${media.name.slice(0, 20)}...${media.name.slice(
                  media.name.length - 10
                )}`
              : media.name}
          </small>

          <Tooltip arrow placement="top" title="Cancel">
            <label htmlFor="messenger-upload-file">
              <IconButton
                size="small"
                color="primary"
                component="span"
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0

                  // '&:hover': {
                  //   backgroundColor: 'gray'
                  // }
                }}
                onClick={() => handleMediaCancel()}
              >
                <CancelOutlined fontSize="small" />
              </IconButton>
            </label>
          </Tooltip>
        </Card>
      )}
      <Divider />
      {startsTicket && (
        <Box sx={{ background: theme.colors.alpha.white[50], px: 2, pt: 2 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={loading}
            inputProps={{ maxLength: TICKET_SUBJECT_MAX, 'aria-label': 'Topic Subject' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Tooltip arrow placement="top" title="New Topic">
                    <ConfirmationNumberOutlinedIcon fontSize="small" color="action" data-testid="composer-ticket-icon" />
                  </Tooltip>
                </InputAdornment>
              )
            }}
          />
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          background: theme.colors.alpha.white[50],
          p: 2
        }}
      >
        <Box flexGrow={1} display="flex" alignItems="center">
          <Avatar
            sx={{ display: compact ? 'none' : { xs: 'none', sm: 'flex' }, mr: 1 }}
            alt={user.name}
            src={PLS}
          />
          <MessageInputWrapper
            autoFocus
            placeholder="Write your message here..."
            fullWidth
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={!!media || loading}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <input
            ref={ref}
            type="file"
            onChange={(e) => handleSelectMedia(e.target.files[0])}
            hidden
            value=""
          />

          <Tooltip arrow placement="top" title="Attach a file">
            <label
              htmlFor="messenger-upload-file"
              onClick={() => ref.current.click()}
            >
              <IconButton sx={{ mx: 1 }} color="primary" component="span">
                <AttachFileTwoToneIcon fontSize="small" />
              </IconButton>
            </label>
          </Tooltip>
          {loading ? (
            <CircularProgress size={20} />
          ) : (
            <Button
              size="small"
              sx={adminActionButtonSx}
              startIcon={<SendTwoToneIcon />}
              variant="contained"
              onClick={() => handleSendMessage()}
            >
              Send
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default BottomBarContent;
